const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;

    // 1. Local Development Mode (localhost / 127.0.0.1)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (envUrl) {
        const clean = envUrl.replace(/\/+$/, '');
        return clean.endsWith('/api') ? clean : `${clean}/api`;
      }
      return `${protocol}//${hostname}:5000/api`;
    }

    // 2. Demo Subdomain Pattern (e.g. demo.freshmindz.in -> demoapi.freshmindz.in/api)
    if (hostname.includes('demo') && !hostname.includes('demoapi')) {
      const apiHost = hostname.replace(/demo(dashboard)?/, 'demoapi');
      return `${protocol}//${apiHost}/api`;
    }

    // 3. Production / Live Server Mode with envUrl set (e.g. REACT_APP_API_URL=https://api.domain.com/api)
    if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
      const clean = envUrl.replace(/\/+$/, '');
      return clean.endsWith('/api') ? clean : `${clean}/api`;
    }

    // 4. Default dynamic relative path '/api' for reverse-proxied production domains
    return '/api';
  }

  if (envUrl) {
    const clean = envUrl.replace(/\/+$/, '');
    return clean.endsWith('/api') ? clean : `${clean}/api`;
  }
  return '/api';
};

export const API_BASE_URL = getApiBaseUrl();

export const SERVER_BASE_URL = (process.env.REACT_APP_SERVER_URL || (API_BASE_URL.startsWith('http') ? API_BASE_URL.replace(/\/api\/?$/, '') : '')).replace(/\/+$/, '');

export const resolveImageUrl = (img) => {
  if (!img) return '';
  if (typeof img !== 'string') return img;

  // Full absolute URLs (http://, https://, data:)
  if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) {
    return img;
  }

  // Bundled static assets (/static/media/...) or public images (/images/...)
  if (img.startsWith('/static/') || img.startsWith('static/') || img.startsWith('/images/') || img.startsWith('images/')) {
    return img.startsWith('/') ? img : `/${img}`;
  }

  // Dynamic relative or absolute path for uploads/database images
  const cleanPath = img.startsWith('/') ? img : `/${img}`;
  if (SERVER_BASE_URL) {
    return `${SERVER_BASE_URL}${cleanPath}`;
  }
  return cleanPath;
};

// Helper for fetch API requests with fallback
async function fetchApi(endpoint, options = {}, fallbackData = null) {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      console.warn(`[API] Endpoint ${endpoint} returned non-JSON response (${contentType}), using default fallback.`);
      return fallbackData;
    }
    const data = await res.json();
    if (endpoint === '/settings') {
      return { settings: data.data || {}, achievements: data.achievements || {} };
    }
    return data.data !== undefined ? data.data : data;
  } catch (err) {
    console.warn(`[API] Endpoint ${endpoint} unreachable, using default fallback:`, err.message);
    return fallbackData;
  }
}

// 1. Sliders API
export const getSliders = async (fallback) => {
  return await fetchApi('/sliders', {}, fallback);
};

// 2. Products API
export const getProducts = async (params = {}, fallback = []) => {
  const query = new URLSearchParams(params).toString();
  const url = query ? `/products?${query}` : '/products';
  return await fetchApi(url, {}, fallback);
};

export const getProductBySlug = async (slug, fallback = null) => {
  return await fetchApi(`/products/${slug}`, {}, fallback);
};

// 3. Projects API
export const getProjects = async (params = {}, fallback = []) => {
  const query = new URLSearchParams(params).toString();
  const url = query ? `/projects?${query}` : '/projects';
  return await fetchApi(url, {}, fallback);
};

export const getProjectBySlug = async (slug, fallback = null) => {
  return await fetchApi(`/projects/${slug}`, {}, fallback);
};

// 4. Blogs API
export const getBlogs = async (params = {}, fallback = []) => {
  const query = new URLSearchParams(params).toString();
  const url = query ? `/blogs?${query}` : '/blogs';
  return await fetchApi(url, {}, fallback);
};

export const getBlogBySlug = async (slug, fallback = null) => {
  return await fetchApi(`/blogs/${slug}`, {}, fallback);
};

// 5. Page Banners API
export const getBannerByKey = async (key, fallback = null) => {
  return await fetchApi(`/page-banners/${key}`, {}, fallback);
};

// 6. Testimonials API
export const getTestimonials = async (fallback = []) => {
  return await fetchApi('/testimonials', {}, fallback);
};

export const submitPublicTestimonial = async (data) => {
  try {
    const res = await fetch(`${API_BASE_URL}/testimonials/public`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return await res.json();
  } catch (err) {
    return { success: false, message: 'Could not connect to server. Please try again later.' };
  }
};

// 7. Contact Inquiries API
export const submitInquiry = async (formData) => {
  try {
    const res = await fetch(`${API_BASE_URL}/inquiries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    return await res.json();
  } catch (err) {
    return { success: false, message: 'Could not connect to backend server. Please try again later.' };
  }
};

// 8. Global Settings & Achievements API
export const getSettings = async (fallback = {}) => {
  return await fetchApi('/settings', {}, fallback);
};

// ==========================================
// ADMIN AUTH & CRUD API SERVICES
// ==========================================

export const adminLogin = async (username, password) => {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    return await res.json();
  } catch (err) {
    return { success: false, message: 'Backend server connection error.' };
  }
};

// Authorized request helper
async function authFetch(endpoint, method = 'GET', body = null, token) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  const options = { method, headers };
  if (body) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE_URL}${endpoint}`, options);
  return await res.json();
}

// Inquiries Admin
export const adminGetInquiries = async (token) => authFetch('/inquiries', 'GET', null, token);
export const adminDeleteInquiry = async (id, token) => authFetch(`/inquiries/${id}`, 'DELETE', null, token);

// Products Admin
export const adminCreateProduct = async (data, token) => authFetch('/products', 'POST', data, token);
export const adminUpdateProduct = async (id, data, token) => authFetch(`/products/${id}`, 'PUT', data, token);
export const adminDeleteProduct = async (id, token) => authFetch(`/products/${id}`, 'DELETE', null, token);

// Sliders Admin
export const adminCreateSlider = async (data, token) => authFetch('/sliders', 'POST', data, token);
export const adminUpdateSlider = async (id, data, token) => authFetch(`/sliders/${id}`, 'PUT', data, token);
export const adminDeleteSlider = async (id, token) => authFetch(`/sliders/${id}`, 'DELETE', null, token);

// Projects Admin
export const adminCreateProject = async (data, token) => authFetch('/projects', 'POST', data, token);
export const adminUpdateProject = async (id, data, token) => authFetch(`/projects/${id}`, 'PUT', data, token);
export const adminDeleteProject = async (id, token) => authFetch(`/projects/${id}`, 'DELETE', null, token);

// Blogs Admin
export const adminCreateBlog = async (data, token) => authFetch('/blogs', 'POST', data, token);
export const adminUpdateBlog = async (id, data, token) => authFetch(`/blogs/${id}`, 'PUT', data, token);
export const adminDeleteBlog = async (id, token) => authFetch(`/blogs/${id}`, 'DELETE', null, token);

// Banners Admin
export const adminUpdateBanner = async (key, data, token) => authFetch(`/page-banners/${key}`, 'PUT', data, token);

// Testimonials Admin
export const adminCreateTestimonial = async (data, token) => authFetch('/testimonials', 'POST', data, token);
export const adminUpdateTestimonial = async (id, data, token) => authFetch(`/testimonials/${id}`, 'PUT', data, token);
export const adminDeleteTestimonial = async (id, token) => authFetch(`/testimonials/${id}`, 'DELETE', null, token);

// Settings Admin
export const adminUpdateSettings = async (data, token) => authFetch('/settings', 'PUT', data, token);

// Image Upload Admin
export const adminUploadImage = async (file, token) => {
  const formData = new FormData();
  formData.append('image', file);
  
  const res = await fetch(`${API_BASE_URL}/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData
  });
  return await res.json();
};